import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import color from "../../../Constants/colors";
import {
  EditorNewsItem,
  NewsCategory,
  NewsPayload,
  NewsStatus,
} from "../../../Models/ambassador";
import { useSaveNewsItem } from "../../../Query/useAmbassador";
import { parseDate } from "../Common/format";
import {
  NEWS_CATEGORIES,
  NEWS_STATUSES,
  useAmbassadorLabels,
} from "../Common/useAmbassadorLabels";
import { NewsArticleBody } from "./NewsArticle";

const EMPTY_NEWS: NewsPayload = {
  title: "",
  teaser: "",
  content: "",
  link: "",
  category: NewsCategory.PRODUCTS,
  status: NewsStatus.DRAFT,
  published_at: null,
};

const pad = (value: number) => String(value).padStart(2, "0");

/** Server time "2026-10-21 14:03:11" as the "2026-10-21T14:03" of a
 * datetime-local input. */
const toInputValue = (value: string | null) => {
  if (!value) return "";
  const date = parseDate(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

interface Props {
  item: EditorNewsItem | null;
  onClose: () => void;
}

const NewsEditorDialog: React.FC<Props> = ({ item, onClose }) => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const saveNews = useSaveNewsItem();
  const [view, setView] = useState<"edit" | "preview">("edit");
  const [data, setData] = useState<NewsPayload>(() =>
    item
      ? {
          title: item.title,
          teaser: item.teaser,
          content: item.content,
          link: item.link,
          category: item.category,
          status: item.status,
          published_at: toInputValue(item.published_at) || null,
        }
      : EMPTY_NEWS,
  );
  const [touched, setTouched] = useState(false);

  const change = (patch: Partial<NewsPayload>) =>
    setData((current) => ({ ...current, ...patch }));
  const titleMissing = !data.title.trim();
  const linkInvalid =
    Boolean(data.link.trim()) && !/^https?:\/\//i.test(data.link.trim());

  const save = () => {
    setTouched(true);
    if (titleMissing || linkInvalid) {
      setView("edit");
      return;
    }
    saveNews.mutate(
      {
        newsId: item?.id ?? null,
        data: {
          ...data,
          link: data.link.trim(),
          // The input holds the browser's local time; send it with its zone.
          published_at: data.published_at
            ? new Date(data.published_at).toISOString()
            : null,
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: color.gray900, fontWeight: 700 }}>
        {item ? t("newsManagement.editTitle") : t("newsManagement.createTitle")}
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={view}
          onChange={(_, value) => setView(value)}
          sx={{ mb: 2, borderBottom: `1px solid ${color.gray200}` }}
        >
          <Tab value="edit" label={t("newsManagement.write")} />
          <Tab value="preview" label={t("newsManagement.preview")} />
        </Tabs>

        {view === "preview" ? (
          <Box
            sx={{
              p: 2,
              border: `1px solid ${color.gray200}`,
              borderRadius: "12px",
            }}
          >
            <NewsArticleBody
              item={{
                id: item?.id ?? 0,
                ...data,
                published_at: data.published_at,
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                label={t("newsManagement.fields.title")}
                value={data.title}
                onChange={(event) => change({ title: event.target.value })}
                required
                fullWidth
                size="small"
                error={touched && titleMissing}
                helperText={
                  touched && titleMissing
                    ? t("wizard.errors.required")
                    : undefined
                }
                inputProps={{ maxLength: 200 }}
              />
            </Box>
            <TextField
              select
              label={t("newsManagement.fields.category")}
              value={data.category}
              onChange={(event) =>
                change({ category: Number(event.target.value) })
              }
              size="small"
            >
              {NEWS_CATEGORIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {labels.newsCategory(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t("newsManagement.fields.status")}
              value={data.status}
              onChange={(event) =>
                change({ status: Number(event.target.value) })
              }
              size="small"
            >
              {NEWS_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {labels.newsStatus(value)}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                label={t("newsManagement.fields.teaser")}
                value={data.teaser}
                onChange={(event) => change({ teaser: event.target.value })}
                fullWidth
                multiline
                minRows={2}
                size="small"
                helperText={t("newsManagement.teaserHint")}
                inputProps={{ maxLength: 500 }}
              />
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                label={t("newsManagement.fields.content")}
                value={data.content}
                onChange={(event) => change({ content: event.target.value })}
                fullWidth
                multiline
                minRows={8}
                size="small"
                helperText={t("newsManagement.contentHint")}
                inputProps={{ maxLength: 20000 }}
              />
            </Box>
            <TextField
              label={t("newsManagement.fields.link")}
              value={data.link}
              onChange={(event) => change({ link: event.target.value })}
              placeholder="https://"
              size="small"
              error={touched && linkInvalid}
              helperText={
                touched && linkInvalid
                  ? t("newsManagement.linkInvalid")
                  : undefined
              }
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              type="datetime-local"
              label={t("newsManagement.fields.publishedAt")}
              value={data.published_at ?? ""}
              onChange={(event) =>
                change({ published_at: event.target.value || null })
              }
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText={t("newsManagement.publishedAtHint")}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={saveNews.isPending}
          onClick={save}
        >
          {data.status === NewsStatus.PUBLISHED
            ? t("newsManagement.savePublished")
            : t("newsManagement.saveDraft")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewsEditorDialog;
